import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { FaPlane, FaEdit, FaTrash, FaPlus, FaCity, FaSearch, FaThLarge, FaList } from "react-icons/fa";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_URL } from "../../config/apiConfig";

const Airports = () => {
  // Get user from Redux store
  const { user } = useSelector((state) => state.auth);

  const [airports, setAirports] = useState([]);
  const [cities, setCities] = useState([]);
  const [cabTypes, setCabTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAirport, setEditingAirport] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    city: "",
    serviceCities: [],
    serviceCityNames: [],
    newServiceCity: "",
    cabTypePrices: {},
    citySpecificPrices: {}, // New structure: { cityName: { cabTypeName: price } }
  });

  // UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("table");

  // Helper function to get authentication token from various storage locations
  const getAuthToken = () => {
    try {
      // First priority: Redux user state token
      if (user && user.token) {
        console.log("Using token from Redux user state");
        return user.token;
      }

      // Try all possible token sources from localStorage
      const possibleTokens = [
        localStorage.getItem("token"),
        localStorage.getItem("driverToken"),
        localStorage.getItem("adminToken"),
        localStorage.getItem("auth_token"),
        localStorage.getItem("admin_token"),
      ];

      // Try parsing user objects for tokens
      try {
        const userObj = JSON.parse(localStorage.getItem("user") || "{}");
        if (userObj.token) {
          console.log("Using token from localStorage user object");
          possibleTokens.unshift(userObj.token); // Add to beginning
        }
      } catch (e) {}

      try {
        const adminObj = JSON.parse(localStorage.getItem("admin") || "{}");
        if (adminObj.token) possibleTokens.push(adminObj.token);
      } catch (e) {}

      try {
        const adminInfoObj = JSON.parse(
          localStorage.getItem("adminInfo") || "{}",
        );
        if (adminInfoObj.token) possibleTokens.push(adminInfoObj.token);
      } catch (e) {}

      try {
        const userInfoObj = JSON.parse(
          localStorage.getItem("userInfo") || "{}",
        );
        if (userInfoObj.token) possibleTokens.push(userInfoObj.token);
      } catch (e) {}

      // Return first valid token found
      for (const token of possibleTokens) {
        if (
          token &&
          token.trim() &&
          token !== "null" &&
          token !== "undefined"
        ) {
          console.log("Using token from localStorage");
          return token.trim();
        }
      }

      // As a last resort for development/testing, try sample admin token
      // Only if we're in development mode
      if (
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"
      ) {
        console.warn("Using sample admin token for development");
        return "sample-admin-token";
      }

      console.error("No valid token found anywhere");
      return null;
    } catch (error) {
      console.error("Error retrieving auth token:", error);
      return null;
    }
  };

  useEffect(() => {
    fetchAirports();
    fetchCities();
    fetchCabTypes();
  }, []);

  const fetchAirports = async () => {
    try {
      const token = getAuthToken();
      const config = token
        ? {
            headers: { Authorization: `Bearer ${token}` },
          }
        : {};

      const response = await axios.get(`${API_URL}/airport`, config);
      const res = response.data || {};
      if (res && (res.success || res.status)) {
        const list = Array.isArray(res.data) ? res.data : Array.isArray(res.airports) ? res.airports : [];
        setAirports(list);
      } else {
        setAirports([]);
      }
    } catch (error) {
      console.error("Error fetching airports:", error);
      if (error.response?.status === 401) {
        toast.error("Authentication required. Please log in as admin.");
      } else {
        toast.error("Failed to fetch airports");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API_URL}/city`);
      const res = response.data || {};
      if (res && (res.success || res.status)) {
        const list = Array.isArray(res.data) ? res.data : Array.isArray(res.cities) ? res.cities : [];
        setCities(list);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  const fetchCabTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/cab`);
      const res = response.data || {};
      if (res && (res.success || res.status)) {
        const list = Array.isArray(res.data) ? res.data : Array.isArray(res.cabs) ? res.cabs : [];
        setCabTypes(list);
      }
    } catch (error) {
      console.error("Error fetching cab types:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Get authentication token
      const token = getAuthToken();

      console.log("Token for airport creation:", token ? "Present" : "Missing");

      if (!token) {
        toast.error("Authentication required. Please log in as admin.");
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      // Use first service city as main city if available
      let mainCityName = "";
      if (formData.serviceCityNames.length > 0) {
        mainCityName = formData.serviceCityNames[0];
      }

      // Auto-generate airport code from airport name if not provided
      const airportCode =
        formData.code ||
        formData.name.slice(0, 3).toUpperCase() +
          Math.floor(Math.random() * 1000);

      // Convert city-specific prices to new format
      const serviceCitiesWithPrices = formData.serviceCityNames.map(
        (cityName) => {
          const cityPrices = {};
          const citySpecificPricing =
            formData.citySpecificPrices[cityName] || {};

          // Convert cab type prices for this city
          cabTypes.forEach((cabType) => {
            const price =
              citySpecificPricing[cabType.name] ||
              citySpecificPricing[cabType._id] ||
              0;
            if (price > 0) {
              cityPrices[cabType.name] = parseInt(price);
            }
          });

          return {
            cityName,
            cabPrices: cityPrices,
          };
        },
      );

      const airportData = {
        code: airportCode,
        name: formData.name,
        mainCity: mainCityName,
        state: "Unknown", // You might want to add state input
        serviceCities: serviceCitiesWithPrices,
      };

      if (editingAirport) {
        await axios.put(
          `${API_URL}/airport/${editingAirport._id}`,
          airportData,
          config,
        );
        toast.success("Airport updated successfully");
      } else {
        await axios.post(`${API_URL}/airport`, airportData, config);
        toast.success("Airport created successfully");
      }

      fetchAirports();
      resetForm();
    } catch (error) {
      console.error("Error saving airport:", error);
      console.error("Error status:", error.response?.status);
      console.error("Error data:", error.response?.data);

      if (error.response?.status === 401) {
        toast.error("Authentication failed. Please log in again as admin.");
        // Optionally redirect to login
        // window.location.href = '/admin/login';
      } else {
        toast.error(error.response?.data?.message || "Failed to save airport");
      }
    }
  };

  const handleEdit = (airport) => {
    setEditingAirport(airport);

    // Convert cabTypePrices array to object for easier form handling
    const cabTypePricesObj = {};
    if (airport.cabTypePrices) {
      airport.cabTypePrices.forEach((ctp) => {
        cabTypePricesObj[ctp.cabTypeId._id || ctp.cabTypeId] = ctp.price;
      });
    }

    // Handle new structure vs legacy structure
    let serviceCityNames = [];
    let citySpecificPrices = {};

    if (airport.serviceCities && airport.serviceCities.length > 0) {
      if (airport.serviceCities[0].cityName) {
        // New structure
        serviceCityNames = airport.serviceCities.map((sc) => sc.cityName);

        // Convert city-specific prices
        airport.serviceCities.forEach((sc) => {
          if (sc.cabPrices) {
            citySpecificPrices[sc.cityName] = {};
            Object.entries(sc.cabPrices).forEach(([cabTypeName, price]) => {
              citySpecificPrices[sc.cityName][cabTypeName] = price;
            });
          }
        });
      } else {
        // Legacy structure
        serviceCityNames = airport.serviceCities.map(
          (city) => city.name || city,
        );
      }
    }

    setFormData({
      code: airport.code || "",
      name: airport.name,
      city: airport.city?._id || "",
      state: airport.state || "",
      serviceCities:
        airport.serviceCities?.map((city) => city._id || city) || [],
      serviceCityNames: serviceCityNames,
      newServiceCity: "",
      cabTypePrices: cabTypePricesObj,
      citySpecificPrices: citySpecificPrices,
    });
    setShowModal(true);
  };

  const handleDelete = async (airportId) => {
    if (window.confirm("Are you sure you want to delete this airport?")) {
      try {
        // Debug: Check all possible token sources
        console.log("=== TOKEN DEBUGGING ===");
        console.log("=== ALL LOCALSTORAGE KEYS ===");
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          const value = localStorage.getItem(key);
          console.log(`${key}:`, value);
        }
        console.log("=== END LOCALSTORAGE ===");

        console.log("Redux user state:", user);
        console.log(
          "localStorage.getItem('token'):",
          localStorage.getItem("token"),
        );
        console.log(
          "localStorage.getItem('adminToken'):",
          localStorage.getItem("adminToken"),
        );
        console.log(
          "localStorage.getItem('driverToken'):",
          localStorage.getItem("driverToken"),
        );

        const userStr = localStorage.getItem("user");
        const adminStr = localStorage.getItem("admin");
        const adminInfoStr = localStorage.getItem("adminInfo");

        console.log("localStorage user object:", userStr);
        console.log("localStorage admin object:", adminStr);
        console.log("localStorage adminInfo object:", adminInfoStr);

        if (userStr) {
          try {
            const userObj = JSON.parse(userStr);
            console.log("Parsed user token:", userObj.token);
          } catch (e) {
            console.log("Error parsing user object:", e);
          }
        }

        if (adminStr) {
          try {
            const adminObj = JSON.parse(adminStr);
            console.log("Parsed admin token:", adminObj.token);
          } catch (e) {
            console.log("Error parsing admin object:", e);
          }
        }

        if (adminInfoStr) {
          try {
            const adminInfoObj = JSON.parse(adminInfoStr);
            console.log("Parsed adminInfo token:", adminInfoObj.token);
          } catch (e) {
            console.log("Error parsing adminInfo object:", e);
          }
        }

        // Get authentication token
        const token = getAuthToken();
        console.log("Final resolved token:", token ? "PRESENT" : "MISSING");
        console.log(
          "Token preview:",
          token ? token.substring(0, 20) + "..." : "NULL",
        );

        if (!token) {
          console.error("No authentication token found!");
          toast.error("Authentication required. Please log in again.");
          return;
        }

        console.log(
          "Making DELETE request to:",
          `${API_URL}/airport/${airportId}`,
        );
        console.log(
          "Using Authorization header:",
          `Bearer ${token.substring(0, 20)}...`,
        );

        const response = await axios.delete(`${API_URL}/airport/${airportId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log("Delete response:", response);
        toast.success("Airport deleted successfully");
        fetchAirports();
      } catch (error) {
        console.error("Error deleting airport:", error);
        console.error("Error response:", error.response);
        console.error("Error status:", error.response?.status);
        console.error("Error data:", error.response?.data);

        if (error.response?.status === 401) {
          toast.error("Authentication failed. Please log in again.");
        } else {
          toast.error("Failed to delete airport");
        }
      }
    }
  };

  const resetForm = () => {
    // Initialize cab type prices for new airport
    const cabTypePricesObj = {};
    cabTypes.forEach((cabType) => {
      cabTypePricesObj[cabType._id] = 0;
    });

    setFormData({
      code: "", // Still needed for backend API
      name: "",
      city: "",
      state: "", // Still needed for backend API
      serviceCities: [],
      serviceCityNames: [],
      newServiceCity: "",
      cabTypePrices: cabTypePricesObj,
      citySpecificPrices: {},
    });
    setEditingAirport(null);
    setShowModal(false);
  };

  const handleServiceCityChange = (cityId) => {
    setFormData((prev) => ({
      ...prev,
      serviceCities: prev.serviceCities.includes(cityId)
        ? prev.serviceCities.filter((id) => id !== cityId)
        : [...prev.serviceCities, cityId],
    }));
  };

  const handleCabTypePriceChange = (cabTypeId, price) => {
    setFormData((prev) => ({
      ...prev,
      cabTypePrices: {
        ...prev.cabTypePrices,
        [cabTypeId]: parseInt(price) || 0,
      },
    }));
  };

  const handleAddServiceCity = () => {
    if (formData.newServiceCity.trim()) {
      setFormData((prev) => ({
        ...prev,
        serviceCityNames: [
          ...prev.serviceCityNames,
          formData.newServiceCity.trim(),
        ],
        newServiceCity: "",
      }));
    }
  };

  const handleRemoveServiceCity = (index) => {
    const cityToRemove = formData.serviceCityNames[index];
    setFormData((prev) => {
      const newCitySpecificPrices = { ...prev.citySpecificPrices };
      delete newCitySpecificPrices[cityToRemove];

      return {
        ...prev,
        serviceCityNames: prev.serviceCityNames.filter((_, i) => i !== index),
        citySpecificPrices: newCitySpecificPrices,
      };
    });
  };

  const handleCitySpecificPriceChange = (cityName, cabTypeName, price) => {
    setFormData((prev) => ({
      ...prev,
      citySpecificPrices: {
        ...prev.citySpecificPrices,
        [cityName]: {
          ...prev.citySpecificPrices[cityName],
          [cabTypeName]: parseInt(price) || 0,
        },
      },
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <FaPlane className="mr-3 text-blue-600" />
          Airport Management
        </h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <FaPlus className="mr-2" />
          Add Airport
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div className="flex items-center gap-3 w-full md:w-1/2">
          <div className="relative flex-1">
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search airports, cities or codes..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <FaSearch className="absolute left-3 top-2 text-gray-400" />
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => setViewMode("table")}
              className={`px-3 py-2 rounded ${viewMode === "table" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"}`}
            >
              Table
            </button>
            <button
              onClick={() => setViewMode("cards")}
              className={`px-3 py-2 rounded ${viewMode === "cards" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"}`}
            >
              Cards
            </button>
          </div>
        </div>
        <div className="text-sm text-gray-500">Showing {airports.length} airports</div>
      </div>

      {/* Airports List */}
      {/* Card Grid (mobile / card view) */}
      <div className={`${viewMode === 'cards' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4' : 'md:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4'}`}>
        {airports.filter(a => {
          const q = searchQuery.trim().toLowerCase();
          if (!q) return true;
          const name = (a.name || '').toLowerCase();
          const code = (a.code || '').toLowerCase();
          const mainCity = (a.mainCity || a.city?.name || '').toString().toLowerCase();
          const services = (a.serviceCities || []).map(s => (s.cityName || s.name || s).toString().toLowerCase()).join(' ');
          return name.includes(q) || code.includes(q) || mainCity.includes(q) || services.includes(q);
        }).map((airport) => (
          <div key={airport._id} className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-800">{airport.name}</div>
                <div className="text-xs text-gray-500 mt-1">{airport.code ? `Code: ${airport.code}` : ''} {airport.mainCity ? ` • ${airport.mainCity}` : ''}</div>
                <div className="mt-3 text-xs text-gray-600">
                  {airport.serviceCities?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {airport.serviceCities.slice(0,5).map((c, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full">{c.cityName || c.name || c}</span>
                      ))}
                      {airport.serviceCities.length > 5 && <span className="text-xs text-gray-400">+{airport.serviceCities.length - 5} more</span>}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">No service cities</span>
                  )}
                </div>
              </div>
              <div className="ml-3 flex flex-col items-end">
                <div className="text-sm font-medium text-green-600">{airport.serviceCities?.length > 0 ? 'Priced' : 'No Price'}</div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => handleEdit(airport)} className="text-blue-600 hover:text-blue-900"><FaEdit /></button>
                  <button onClick={() => handleDelete(airport._id)} className="text-red-600 hover:text-red-900"><FaTrash /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={`${viewMode === 'cards' ? 'hidden md:block' : ''} bg-white rounded-lg shadow overflow-hidden`}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Airport Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                City
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Service Cities
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cab Pricing
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {airports.filter(a => {
              const q = searchQuery.trim().toLowerCase();
              if (!q) return true;
              const name = (a.name || '').toLowerCase();
              const code = (a.code || '').toLowerCase();
              const mainCity = (a.mainCity || a.city?.name || '').toString().toLowerCase();
              const services = (a.serviceCities || []).map(s => (s.cityName || s.name || s).toString().toLowerCase()).join(' ');
              return name.includes(q) || code.includes(q) || mainCity.includes(q) || services.includes(q);
            }).map((airport) => (
              <tr key={airport._id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {airport.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {airport.mainCity ||
                    (airport.serviceCities && airport.serviceCities.length > 0
                      ? airport.serviceCities[0].cityName ||
                        airport.serviceCities[0].name ||
                        airport.serviceCities[0]
                      : airport.city?.name)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  <div className="flex flex-wrap gap-1">
                    {airport.serviceCities?.length > 0 ? (
                      airport.serviceCities.map((city, index) => (
                        <span
                          key={city._id || city.cityName || index}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                        >
                          {city.cityName || city.name || city}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-500">
                        No service cities
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  <div className="space-y-2">
                    {airport.serviceCities?.length > 0 &&
                    airport.serviceCities[0]?.cabPrices ? (
                      // New structure - show city-specific pricing
                      airport.serviceCities.map((serviceCity, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded p-2 bg-gray-50"
                        >
                          <div className="text-xs font-medium text-gray-700 mb-1">
                            {serviceCity.cityName}
                          </div>
                          <div className="space-y-1">
                            {Object.entries(serviceCity.cabPrices || {}).map(
                              ([cabType, price]) => (
                                <div
                                  key={cabType}
                                  className="flex justify-between items-center text-xs"
                                >
                                  <span className="text-gray-600">
                                    {cabType}
                                  </span>
                                  <span className="font-medium text-green-600">
                                    ₹{price}
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      ))
                    ) : airport.cabTypePrices &&
                      airport.cabTypePrices.length > 0 ? (
                      // Legacy structure
                      airport.cabTypePrices.map((ctp) => (
                        <div
                          key={ctp.cabTypeId._id || ctp.cabTypeId}
                          className="flex justify-between items-center bg-gray-50 p-2 rounded text-xs"
                        >
                          <span className="text-gray-800">
                            {ctp.cabTypeId.name || "Unknown Cab Type"}
                          </span>
                          <span className="font-medium text-green-600">
                            ₹{ctp.price}
                          </span>
                        </div>
                      ))
                    ) : (
                      <span className="text-xs text-gray-500">
                        No prices set
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleEdit(airport)}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(airport._id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingAirport ? "Edit Airport" : "Add New Airport"}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Airport Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Cities (where airport provides transfer)
                  </label>
                  <div className="space-y-3">
                    {/* Add new service city */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.newServiceCity || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            newServiceCity: e.target.value,
                          })
                        }
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Enter service city name"
                      />
                      <button
                        type="button"
                        onClick={handleAddServiceCity}
                        className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                      >
                        Add
                      </button>
                    </div>

                    {/* List of added service cities */}
                    <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
                      {formData.serviceCityNames &&
                      formData.serviceCityNames.length > 0 ? (
                        formData.serviceCityNames.map((cityName, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between mb-2 p-2 bg-gray-50 rounded"
                          >
                            <span className="text-sm text-gray-700">
                              {cityName}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveServiceCity(index)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No service cities added yet
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* City-Specific Cab Pricing */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Set Cab Prices for Each Service City
                  </label>
                  <div className="space-y-4 max-h-96 overflow-y-auto border border-gray-300 rounded-md p-3">
                    {formData.serviceCityNames.length > 0 ? (
                      formData.serviceCityNames.map((cityName, cityIndex) => (
                        <div
                          key={cityIndex}
                          className="bg-gray-50 p-4 rounded-lg border"
                        >
                          <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                            <FaCity className="mr-2 text-blue-600" />
                            {cityName} - Cab Pricing
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {cabTypes.map((cabType) => (
                              <div
                                key={cabType._id}
                                className="flex items-center justify-between p-2 bg-white border rounded"
                              >
                                <div className="flex-1">
                                  <span className="text-xs font-medium text-gray-700">
                                    {cabType.name}
                                  </span>
                                  <span className="text-xs text-gray-500 block">
                                    {cabType.acType} - {cabType.seatingCapacity}{" "}
                                    seats
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-gray-600">
                                    ₹
                                  </span>
                                  <input
                                    type="number"
                                    value={
                                      formData.citySpecificPrices[cityName]?.[
                                        cabType.name
                                      ] ||
                                      formData.citySpecificPrices[cityName]?.[
                                        cabType._id
                                      ] ||
                                      0
                                    }
                                    onChange={(e) =>
                                      handleCitySpecificPriceChange(
                                        cityName,
                                        cabType.name,
                                        e.target.value,
                                      )
                                    }
                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="0"
                                    min="0"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        <FaCity className="mx-auto text-gray-400 text-2xl mb-2" />
                        <p className="text-sm">
                          Add service cities first to set cab prices for each
                          city
                        </p>
                      </div>
                    )}
                    {cabTypes.length === 0 && (
                      <div className="text-center text-gray-500 py-4">
                        No cab types available
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingAirport ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Airports;
